// ecom-message.ts — the PURE read-model + parser for the in-mission E-Com overlay
// (HTML/DOM UI shell plan, Phase 5, Form 6). Reproduces the canvas EcomTextControl
// DrawEcomText speaker segmentation (ui/ecom.ts:496-572) WITHOUT a canvas/DOM/font:
//   - the message is broken into speeches separated by '@A'/'@J'/'@O'/'@F' markers;
//   - each segment renders in that speaker's COLOR (GetColor(kiclr{Andy,Jana,
//     Olstrom,Fox}) → a CSS rgb) and is PREFIXED with the speaker NAME
//     ("Andy: " / "Jana: " / …, s_aszNames);
//   - text before any marker (or under an unknown marker) is WHITE with no name
//     (the C++ default color, no Name draw);
//   - the 2-char '@X' markers are NOT counted in the REVEALED length (DrawEcomText
//     `cchMax -= 2`), but the speaker-Name prefix IS shown in full as soon as the
//     marker is reached (the Name is drawn before the speech chars stream).
//
// This is the SAME logic the DOM typewriter reveals progressively: `revealLength`
// counts only the message's NON-marker characters; given a revealed count, the
// segments report how many of THEIR own characters are currently visible so the
// view can render "Andy: hel" → "Andy: hello" as the count climbs.
//
// Pure (no DOM) so the parsing — markers→colored+named segments, '@X' not counted,
// the address "@@"→"@" expansion — is unit-tested (dom-ecom.test.ts).

import { GetColor, iclr } from '../../gfx/palette.ts';

// ui/ecom.ts:54-77 — the speaker tables (kept in sync with the canvas).
const s_aszEmailAddrs = [
  'Nobody',
  'Andy@@ACME',
  'Jana@@ACME',
  'Olstrom@@ACME',
  'Fox@@OMNI',
  'ACME Security',
  'OMNI Security',
  'Anonymous',
  '',
];
const s_aszNames = [
  '',
  'Andy: ',
  'Jana: ',
  'Olstrom: ',
  'Fox: ',
  'ACME Security: ',
  'OMNI Security: ',
  'Anonymous: ',
  '',
];
const s_aszPortraits: (string | null)[] = [
  null,
  'andyportrait.png',
  'jana.png',
  'olstrom.png',
  'fox.png',
  null,
  null,
  null,
  null,
];

/** The CSS color for a speaker marker index (the s_aszNames index). 1=Andy 2=Jana
 *  3=Olstrom 4=Fox; everything else (incl. 0/no-speaker) is WHITE — the C++
 *  DrawEcomText default clr (ui/ecom.ts:505). */
function speakerColor(iName: number): string {
  let c;
  switch (iName) {
    case 1:
      c = GetColor(iclr.kiclrAndy);
      break;
    case 2:
      c = GetColor(iclr.kiclrJana);
      break;
    case 3:
      c = GetColor(iclr.kiclrOlstrom);
      break;
    case 4:
      c = GetColor(iclr.kiclrFox);
      break;
    default:
      c = GetColor(iclr.kiclrWhite);
      break;
  }
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}

/** The '@X' marker letter → the s_aszNames index (Andy/Jana/Olstrom/Fox); an
 *  unknown letter is 0 (no name, default color) — matches the DrawEcomText switch
 *  (ui/ecom.ts:521-526), which leaves iszNames=0 / the prior color for unknowns. */
function markerNameIndex(letter: string): number {
  switch (letter) {
    case 'A':
      return 1;
    case 'J':
      return 2;
    case 'O':
      return 3;
    case 'F':
      return 4;
    default:
      return 0;
  }
}

/** The From/To address line for a character index, with the data-file "@@"→"@"
 *  collapse applied (ui/ecom.ts:66 — "Olstrom@@ACME" → "Olstrom@ACME"). Out-of-range
 *  indices yield ''. */
export function ecomAddress(idx: number): string {
  const a = s_aszEmailAddrs[idx];
  return a === undefined ? '' : a.replace(/@@/g, '@');
}

/** The portrait bitmap name for a character index (LARGE only), or null when the
 *  character has no portrait (ui/ecom.ts:74-77). */
export function ecomPortrait(idx: number): string | null {
  return s_aszPortraits[idx] ?? null;
}

/** One parsed speech segment: a contiguous run in ONE color, optionally prefixed
 *  by a speaker name. `markerStart`/`markerEnd` are the indices in the SOURCE
 *  message string that bound this segment's NON-marker text (so the view can map a
 *  revealed-character count onto each segment); the 2-char '@X' marker itself sits
 *  just before `markerStart` and is NOT part of the revealed length. */
export interface EcomSegment {
  /** the speaker name prefix ("Andy: " / "" for the default/no-speaker run). */
  name: string;
  /** the CSS color this segment renders in. */
  color: string;
  /** the segment's MESSAGE TEXT (no marker, no name) — the chars that stream in. */
  text: string;
}

/** The whole parsed E-Com message: the ordered colored+named segments, plus the
 *  TOTAL number of revealable (non-marker) characters (the typewriter's reveal
 *  budget — matches the canvas, where the '@X' markers do not count). */
export interface ParsedEcomMessage {
  segments: EcomSegment[];
  /** Σ segment.text.length — the canvas reveal budget (markers excluded). */
  revealLength: number;
}

/** Parse a message into colored+named segments (the DrawEcomText segmentation,
 *  ui/ecom.ts:496-572), counting ONLY the non-marker characters toward the reveal
 *  length. The first segment carries no name unless it opens with a marker; an
 *  unknown '@X' becomes a default-color, no-name segment (the C++ keeps the prior
 *  color, but a fresh message starts white — faithful for the shipped data, whose
 *  speeches all open with a known marker). */
export function parseEcomMessage(message: string): ParsedEcomMessage {
  const segments: EcomSegment[] = [];
  let revealLength = 0;
  let i = 0;

  // a leading run BEFORE any marker (or the whole string if there are none) is a
  // default white, no-name segment (DrawEcomText's `psz[iNextSpeech] != '@'` arm).
  while (i < message.length) {
    let name = '';
    let color = speakerColor(0);
    if (message[i] === '@') {
      const iName = markerNameIndex(message[i + 1] ?? '');
      name = s_aszNames[iName] ?? '';
      color = speakerColor(iName);
      i += 2; // skip the 2-char '@X' marker — NOT counted in the reveal length
    }
    // the speech text runs to the next '@' (or end).
    const start = i;
    while (i < message.length && message[i] !== '@') i++;
    const text = message.slice(start, i);
    segments.push({ name, color, text });
    revealLength += text.length;
  }

  return { segments, revealLength };
}

/** Slice the parsed message to the first `revealed` MESSAGE characters (markers
 *  excluded), returning the visible segments in order. A segment's NAME prefix
 *  appears as soon as the reveal REACHES that segment's start (i.e. all earlier
 *  segments' text is fully revealed) — the canvas draws the Name immediately at a
 *  speech start, before the speech chars stream (ui/ecom.ts:534). A segment with no
 *  visible text AND no name is dropped (an empty default-color run). */
export function revealSegments(parsed: ParsedEcomMessage, revealed: number): EcomSegment[] {
  const out: EcomSegment[] = [];
  const budget = Math.max(0, Math.trunc(revealed));
  let consumed = 0; // message chars revealed so far across prior segments
  for (const seg of parsed.segments) {
    if (consumed > budget) break; // the reveal hasn't reached this segment
    const take = Math.min(seg.text.length, budget - consumed);
    // the Name shows once the reveal has REACHED this segment (consumed <= budget);
    // its text streams in up to `take`. Drop a no-name segment with nothing visible.
    if (seg.name !== '' || take > 0) {
      out.push({ name: seg.name, color: seg.color, text: seg.text.slice(0, Math.max(0, take)) });
    }
    consumed += seg.text.length;
  }
  return out;
}

/** One segment prepared for STABLE-LAYOUT reveal: the FULL `name` + FULL `text` are
 *  always carried (so the box can wrap on the COMPLETE page content and NEVER reflow as
 *  the reveal climbs), tagged with how much of each is currently visible:
 *    - `nameVisible` — the speaker-name prefix shows WHOLE the moment the reveal REACHES
 *      this segment (the same rule as revealSegments: consumed <= revealed); before that
 *      it is drawn in the INVISIBLE style so its layout box is still reserved;
 *    - `visibleCount` — how many of `text`'s leading chars are revealed; the remainder is
 *      drawn invisibly (still occupying its box).
 *  The view splits each segment on this boundary into a visible run + an invisible run; as
 *  the boundary marches forward NOTHING reflows, because the total glyph box is constant. */
export interface EcomRevealSegment {
  name: string;
  color: string;
  text: string;
  nameVisible: boolean;
  visibleCount: number;
}

/** The STABLE-LAYOUT counterpart to revealSegments(): EVERY segment is returned with its
 *  full name+text plus the visible split for `revealed`, so the view can render the WHOLE
 *  page (wrapping fixed from the first frame) and merely toggle each character between the
 *  visible and the invisible style. Same visibility rule as revealSegments — this is the
 *  partition (visible ⊎ invisible) of the same content revealSegments returns the visible
 *  half of. */
export function layoutReveal(parsed: ParsedEcomMessage, revealed: number): EcomRevealSegment[] {
  const budget = Math.max(0, Math.trunc(revealed));
  let consumed = 0;
  return parsed.segments.map((seg) => {
    const nameVisible = consumed <= budget; // the speech start has been reached
    const visibleCount = Math.max(0, Math.min(seg.text.length, budget - consumed));
    consumed += seg.text.length;
    return { name: seg.name, color: seg.color, text: seg.text, nameVisible, visibleCount };
  });
}
